/**
 * The Algolia fields the product tile reads (organisms/product/product-tile + helpers/algolia/map-product)
 * — the ONE `attributesToRetrieve` base for every product listing, on both paths:
 *
 *   - server path: V2 listing (+ its configured facet paths, plp-dataset-core), V2 slider / grid
 *     (fetch-slider-dataset), PDP related products (+ `key` and `attributes.IsNew`, fetch-related-products);
 *   - browser path: /search (+ its Studio facet keys, client-retrieve-attributes).
 *
 * Every listing renders the same tile, so this list must not fork per surface: a surface adds only what
 * IT needs on top (facet paths to count, a field it filters or orders by). Editing this list changes the
 * Redis cache key of every server-path listing (the params ARE the key, backend AlgoliaApi), so each one
 * misses the cache once after deploy — harmless, but expect one round of cold Algolia fetches.
 *
 * It is also the biggest single lever on page weight: the retrieved hit IS the payload (Redis entry, SSR
 * HTML, edge cache). Measured on one 253-hit category: `['*']` returned 3,497 KB, a list like this 554 KB.
 *
 * Paths are the CANONICAL `attributes.*` spelling, never the `searchableAttributes.*` duplicate (see
 * attribute-paths.ts). The backend's slimForCache drops that duplicate anyway, and mapProduct only lifts
 * `hit.attributes` onto the product.
 *
 * Deliberately NOT here (verified against the index, not assumed):
 *   - `attributes.IsNew` — no tile reads it; the indices already rank new products first. Only PDP related
 *     products adds it, because its backend pick is shuffled (see that tastic).
 *   - `attributes.item-name` / `attributes.item-code` — the tile prefers these over `name` and the variant
 *     code, but they do not exist at product level on any record, so the tile always falls through to
 *     `name` + `variants[].attributes['item-code']`.
 *   - `variants.attributes.item-name` — requested by the old listings, read by none of them.
 *   - `variants.attributes.lifecycle` — the "While Stocks Last" badge that consumed it no longer exists.
 *   - `productId` — mapProduct falls back to `objectID`, which Algolia always returns.
 */
export const TILE_ATTRIBUTES = [
  // Product level
  'name', // tile title, image alt, and the name_asc / name_desc sort
  'slug', // PDP URL
  'categoryKeys', // category label on the tile (mapped to `categories[]`)
  'masterVariantID', // picks the default variant
  'attributes.builder-range', // builder-range products hide the RRP line
  // Variant level
  'variants.id', // master-variant match + swatch keys
  'variants.key', // product-code fallback on the static (off-screen) tile
  'variants.sku', // PDP URL, wishlist line item, click tracking
  'variants.images', // tile image + carousel
  'variants.prices', // the "RRP" line
  'variants.attributes.colour', // swatches, PDP URL, colour-filter variant, chrome/stainless default variant
  'variants.attributes.item-code', // "Product code" line, ShopOnline, Product-IDs variant matching
  'variants.attributes.variant-name', // swatch dropdown label, colour-filter variant fallback
  'variants.attributes.builder-range',
];
